/**
 * Location Picker Modal
 * Shown when a job is dragged to the schedule and has no contact_location set.
 * Fetches available locations for the job's contact and lets the user pick one.
 * If cancelled or no locations available, the drop is aborted.
 */

'use client';

import React, { useState, useEffect } from 'react';
import { X, MapPin, Building2, Navigation, Loader2, AlertTriangle, Plus } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import QuickLocationForm from '@/components/work-items/QuickLocationForm';
import type { CreatedLocation } from '@/components/work-items/QuickLocationForm';

import LocationAutocomplete, { type CustomLocationData, CustomLocationResult } from '@/components/maps/LocationAutocomplete';

interface ContactLocation {
  id: number;
  contact_id: number;
  location_name: string;
  location_type: string;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  radius_meters: number | null;
  is_primary: boolean;
  is_active: boolean;
  contact_person: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  no_appointment_required: boolean;
}

interface LocationPickerModalProps {
  isOpen: boolean;
  contactId: number | null;
  contactName: string | null;
  jobTitle: string;
  onSelect: (locationId: number | null, locationAddress: string | null, customLocation?: CustomLocationData) => void;
  onCancel: () => void;
}

export default function LocationPickerModal({
  isOpen,
  contactId,
  contactName,
  jobTitle,
  onSelect,
  onCancel,
}: LocationPickerModalProps) {
  const [locations, setLocations] = useState<ContactLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'contact' | 'custom'>('contact');
  const [customLocation, setCustomLocation] = useState<CustomLocationData | null>(null);
  const [customLocSearch, setCustomLocSearch] = useState('');
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';

  // Google Places .pac-container renders at z-index ~1000, below our z-[9999] modal.
  // Inject a temporary style override while the custom tab is active.
  useEffect(() => {
    if (!isOpen || activeTab !== 'custom') return;
    const styleId = 'pac-z-fix-location-picker';
    let el = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = styleId;
      document.head.appendChild(el);
    }
    el.textContent = '.pac-container { z-index: 10000 !important; }';
    return () => {
      document.getElementById(styleId)?.remove();
    };
  }, [isOpen, activeTab]);

  // Fetch locations when modal opens
  useEffect(() => {
    if (!isOpen) {
      setLocations([]);
      setSelectedId(null);
      setError(null);
      setShowCreateForm(false);
      setActiveTab('contact');
      setCustomLocation(null);
      setCustomLocSearch('');
      return;
    }

    if (!contactId) {
      setError('This job has no contact assigned. Please assign a contact with a location before scheduling.');
      return;
    }

    const fetchLocations = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) throw new Error('Not authenticated');

        const res = await fetch(`/api/v1/operations/locations?contact_id=${contactId}&with_contacts=true`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!res.ok) throw new Error('Failed to fetch locations');

        const data = await res.json();
        const locs: ContactLocation[] = data.data || [];
        setLocations(locs);

        if (locs.length === 0) {
          setError('No locations found for this contact. Please add a location to the contact before scheduling.');
        } else if (locs.length === 1) {
          // Auto-select if only one location
          setSelectedId(locs[0].id);
        }
      } catch (err: any) {
        console.error('[LocationPickerModal] Error fetching locations:', err);
        setError(err.message || 'Failed to load locations');
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, [isOpen, contactId]);

  if (!isOpen) return null;

  const formatAddress = (loc: ContactLocation): string => {
    const parts = [
      loc.address_line1,
      loc.address_line2,
      loc.city,
      loc.state,
      loc.postal_code,
    ].filter(Boolean);
    return parts.join(', ') || 'No address';
  };

  const getLocationTypeIcon = (type: string) => {
    switch (type) {
      case 'office':
        return <Building2 className="w-4 h-4" />;
      case 'site':
      case 'warehouse':
        return <Navigation className="w-4 h-4" />;
      default:
        return <MapPin className="w-4 h-4" />;
    }
  };

  const handleConfirm = () => {
    if (activeTab === 'custom') {
      if (!customLocation) return;
      onSelect(null, customLocation.address, customLocation);
    } else {
      if (!selectedId) return;
      const loc = locations.find(l => l.id === selectedId);
      if (!loc) return;
      onSelect(loc.id, formatAddress(loc));
    }
  };

  const handleSkip = () => {
    onSelect(null, null);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className={`relative w-full max-w-lg mx-4 rounded-xl shadow-2xl border ${
        isDark
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-500" />
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Select Location
            </h3>
          </div>
          <button
            onClick={onCancel}
            className={`p-1.5 rounded-lg transition-colors ${
              isDark
                ? 'hover:bg-gray-700 text-gray-400'
                : 'hover:bg-gray-100 text-gray-500'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {/* Context info */}
          <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <strong>{jobTitle}</strong> has no location set.
            {contactName && <> Select a location for <strong>{contactName}</strong>:</>}
          </p>

          {/* Tab toggle */}
          <div className={`flex rounded-lg p-0.5 mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <button
              onClick={() => setActiveTab('contact')}
              className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all ${
                activeTab === 'contact'
                  ? isDark ? 'bg-gray-600 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm'
                  : isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Contact Location
            </button>
            <button
              onClick={() => setActiveTab('custom')}
              className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all ${
                activeTab === 'custom'
                  ? isDark ? 'bg-orange-600 text-white shadow-sm' : 'bg-orange-500 text-white shadow-sm'
                  : isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Custom Override
            </button>
          </div>

          {/* Custom location tab */}
          {activeTab === 'custom' && (
            <div className="space-y-3">
              <LocationAutocomplete
                value={customLocSearch}
                onChange={setCustomLocSearch}
                onPlaceSelect={(data) => { setCustomLocation(data); setCustomLocSearch(data.name); }}
                onClear={() => { setCustomLocation(null); setCustomLocSearch(''); }}
                placeholder="Search address or place name..."
                isDark={isDark}
              />
              {customLocation && (
                <CustomLocationResult
                  location={customLocation}
                  onClear={() => { setCustomLocation(null); setCustomLocSearch(''); }}
                  isDark={isDark}
                />
              )}
              <div className={`flex items-start gap-2 p-3 rounded-lg text-xs ${
                isDark ? 'bg-blue-900/20 border border-blue-700/30 text-blue-300' : 'bg-blue-50 border border-blue-200 text-blue-700'
              }`}>
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>This location will be saved on the appointment only — not added to the contact&apos;s saved locations.</span>
              </div>
            </div>
          )}

          {/* Contact location tab content */}
          {activeTab === 'contact' && (
            <>
          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              <span className={`ml-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Loading locations...
              </span>
            </div>
          )}

          {/* Error / No locations */}
          {error && !loading && (
            <div className={`flex items-start gap-3 p-4 rounded-lg ${
              isDark ? 'bg-amber-900/20 border border-amber-700/30' : 'bg-amber-50 border border-amber-200'
            }`}>
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                  {error}
                </p>
                {contactId && !showCreateForm && (
                  <button
                    onClick={() => { setShowCreateForm(true); setError(null); }}
                    className="mt-2 text-sm font-medium text-blue-500 hover:text-blue-400 flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Create a new location
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Create location modal */}
          {showCreateForm && contactId && (
            <QuickLocationForm
              contactId={contactId}
              onCreated={(newLoc: CreatedLocation) => {
                const mapped: ContactLocation = {
                  ...newLoc,
                  address_line2: newLoc.address_line2 || null,
                  radius_meters: newLoc.radius_meters ?? null,
                  contact_person: null,
                  contact_email: null,
                  contact_phone: null,
                  no_appointment_required: false,
                };
                setLocations(prev => [...prev, mapped]);
                setSelectedId(newLoc.id);
                setShowCreateForm(false);
                setError(null);
              }}
              onCancel={() => setShowCreateForm(false)}
            />
          )}

          {/* Location list */}
          {!loading && !error && locations.length > 0 && (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {locations.map((loc) => {
                const isSelected = selectedId === loc.id;
                return (
                  <button
                    key={loc.id}
                    onClick={() => setSelectedId(loc.id)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? isDark
                          ? 'border-blue-500 bg-blue-900/30'
                          : 'border-blue-500 bg-blue-50'
                        : isDark
                          ? 'border-gray-700 bg-gray-750 hover:border-gray-600 hover:bg-gray-700'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 ${
                        isSelected ? 'text-blue-500' : isDark ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {getLocationTypeIcon(loc.location_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium text-sm ${
                            isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                            {loc.location_name}
                          </span>
                          {loc.is_primary && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-medium">
                              Primary
                            </span>
                          )}
                          {loc.location_type && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {loc.location_type}
                            </span>
                          )}
                        </div>
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatAddress(loc)}
                        </p>
                        {loc.contact_person && (
                          <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Contact: {loc.contact_person}
                            {loc.contact_phone && ` · ${loc.contact_phone}`}
                          </p>
                        )}
                      </div>
                      {/* Selection indicator */}
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500'
                          : isDark
                            ? 'border-gray-600'
                            : 'border-gray-300'
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Create new location button - shown when locations exist and form is not open */}
          {!loading && !showCreateForm && contactId && (
            <button
              onClick={() => setShowCreateForm(true)}
              className={`mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border-2 border-dashed transition-colors ${
                isDark
                  ? 'border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400 hover:bg-blue-900/20'
                  : 'border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Plus className="w-4 h-4" />
              Create New Location
            </button>
          )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between gap-3 px-5 py-4 border-t ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <button
            onClick={handleSkip}
            className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
              isDark
                ? 'text-gray-500 hover:bg-gray-700 hover:text-gray-400'
                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
            }`}
          >
            Skip Location
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                isDark
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
            {(activeTab === 'contact' ? locations.length > 0 : true) && (
              <button
                onClick={handleConfirm}
                disabled={activeTab === 'contact' ? !selectedId : !customLocation}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  (activeTab === 'contact' ? !!selectedId : !!customLocation)
                    ? activeTab === 'custom'
                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                    : isDark
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Confirm Location
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
